import React, { useCallback, useEffect, useState } from 'react'
import { XMarkIcon, CheckIcon } from '@heroicons/react/24/outline'
import { Heading, Text, Button, Input, LoadingSpinner } from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import { useUserLogs } from '../../hooks/useUserLogs'
import { modulesService } from '../../services/modules'
import { marketingService } from '../../services/marketing'
import { BRAND_COLOR } from '../../constants'
import type {
  MarketingClippingCatalogItem,
  MarketingClippingCategory,
  MarketingClippingVehicle,
} from '../../types/marketing'
import ClippingParametrosTab from './ClippingParametrosTab'

const MODULE_NAME = 'marketing_parametros'
const PERM_LOADING: React.ReactNode = null

type CatalogKind = 'veiculo' | 'categoria'
type CatalogSelectContext = 'search_config' | 'search_site'

interface CatalogSelectTarget {
  kind: CatalogKind
  context: CatalogSelectContext
}

function catalogFormTitle(kind: CatalogKind): string {
  return kind === 'veiculo' ? 'Novo veículo' : 'Nova categoria'
}

function catalogLogType(kind: CatalogKind) {
  if (kind === 'veiculo') return 'marketing_clipping_vehicle' as const
  return 'marketing_clipping_category' as const
}

function sortCatalog<T extends MarketingClippingCatalogItem>(list: T[]): T[] {
  return [...list].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
}

const MarketingParametrosPage: React.FC = () => {
  const { logCreate } = useUserLogs()

  const [moduleTitle, setModuleTitle] = useState('Parâmetros')
  const [moduleDescription, setModuleDescription] = useState(
    'Parâmetros de busca automática e análise de IA do clipping'
  )

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [veiculos, setVeiculos] = useState<MarketingClippingVehicle[]>([])
  const [categorias, setCategorias] = useState<MarketingClippingCategory[]>([])

  const [catalogModal, setCatalogModal] = useState<CatalogKind | null>(null)
  const [catalogSelectTarget, setCatalogSelectTarget] = useState<CatalogSelectTarget | null>(null)
  const [pendingCatalogSelection, setPendingCatalogSelection] = useState<{
    kind: CatalogKind
    id: string
    context: CatalogSelectContext
  } | null>(null)
  const [catalogName, setCatalogName] = useState('')
  const [catalogSaving, setCatalogSaving] = useState(false)
  const [catalogError, setCatalogError] = useState<string | null>(null)

  useEffect(() => {
    modulesService.getByName(MODULE_NAME).then((result) => {
      if (result.success && result.data) {
        if (result.data.display_name) setModuleTitle(result.data.display_name)
        if (result.data.description) setModuleDescription(result.data.description)
      }
    })
  }, [])

  const loadCatalogs = useCallback(async (opts?: { showOverlay?: boolean }) => {
    const showOverlay = opts?.showOverlay ?? true
    try {
      if (showOverlay) setLoading(true)
      setError(null)
      const [veiculosData, categoriasData] = await Promise.all([
        marketingService.listVehicles(false),
        marketingService.listCategories(false),
      ])
      setVeiculos(sortCatalog(veiculosData))
      setCategorias(sortCatalog(categoriasData))
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar parâmetros')
    } finally {
      if (showOverlay) setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadCatalogs()
  }, [loadCatalogs])

  const openCatalogModal = (kind: CatalogKind, context: CatalogSelectContext) => {
    setCatalogModal(kind)
    setCatalogSelectTarget({ kind, context })
    setCatalogName('')
    setCatalogError(null)
  }

  const closeCatalogForm = () => {
    setCatalogModal(null)
    setCatalogSelectTarget(null)
    setCatalogName('')
    setCatalogError(null)
  }

  const clearPendingCatalogSelection = useCallback(() => {
    setPendingCatalogSelection(null)
  }, [])

  const saveCatalog = async () => {
    if (!catalogModal || !catalogSelectTarget) return
    const name = catalogName.trim()
    if (!name) {
      setCatalogError('Informe o nome')
      return
    }
    const selectTarget = catalogSelectTarget
    try {
      setCatalogSaving(true)
      setCatalogError(null)

      if (catalogModal === 'veiculo') {
        const created = await marketingService.createVehicle({ name })
        setVeiculos((prev) => sortCatalog([...prev, created]))
        setPendingCatalogSelection({
          kind: 'veiculo',
          id: created.id,
          context: selectTarget.context,
        })
        void logCreate(
          catalogLogType('veiculo'),
          created.id,
          `Criou veículo de clipping: ${created.name}`,
          { name: created.name, is_active: created.is_active }
        )
      } else {
        const created = await marketingService.createCategory({ name })
        setCategorias((prev) => sortCatalog([...prev, created]))
        setPendingCatalogSelection({
          kind: 'categoria',
          id: created.id,
          context: selectTarget.context,
        })
        void logCreate(
          catalogLogType('categoria'),
          created.id,
          `Criou categoria de clipping: ${created.name}`,
          { name: created.name, is_active: created.is_active }
        )
      }
      closeCatalogForm()
    } catch (err: unknown) {
      setCatalogError(err instanceof Error ? err.message : 'Erro ao salvar')
    } finally {
      setCatalogSaving(false)
    }
  }

  return (
    <div className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto">
        <PermissionGuard
          moduleName={MODULE_NAME}
          permissionType="show"
          loadingComponent={PERM_LOADING}
        >
          <div className="py-6 px-4 sm:px-6 lg:px-8">
            <div className="mb-6">
              <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                {moduleTitle}
              </Heading>
              <Text size="sm" color="muted" className="mt-2">
                {moduleDescription}
              </Text>
            </div>

            {error && (
              <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-400">
                {error}
              </div>
            )}

            {loading ? (
              <div className="flex justify-center py-16">
                <LoadingSpinner />
              </div>
            ) : (
              <ClippingParametrosTab
                moduleName={MODULE_NAME}
                categorias={categorias}
                veiculos={veiculos}
                onVehiclesChange={setVeiculos}
                onCategoriesChange={setCategorias}
                onOpenCatalog={openCatalogModal}
                pendingCatalogSelection={pendingCatalogSelection}
                onPendingCatalogSelectionConsumed={clearPendingCatalogSelection}
              />
            )}
          </div>

          {catalogModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                  <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                    {catalogFormTitle(catalogModal)}
                  </Heading>
                  <Button
                    onClick={closeCatalogForm}
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-1"
                    disabled={catalogSaving}
                  >
                    <XMarkIcon className="h-4 w-4" />
                    Fechar
                  </Button>
                </div>

                <div className="p-6 overflow-y-auto flex-1 space-y-4">
                  {catalogError && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
                      {catalogError}
                    </div>
                  )}
                  <div>
                    <Text size="sm" className="mb-2">
                      Nome <span className="text-red-500">*</span>
                    </Text>
                    <Input
                      value={catalogName}
                      onChange={(e) => setCatalogName(e.target.value)}
                      placeholder="Digite o nome..."
                      className={catalogError ? 'border-red-500' : ''}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          void saveCatalog()
                        }
                      }}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
                  <Button onClick={closeCatalogForm} variant="outline" disabled={catalogSaving}>
                    Cancelar
                  </Button>
                  <PermissionGuard
                    moduleName={MODULE_NAME}
                    permissionType="insert"
                    fallback={null}
                    loadingComponent={PERM_LOADING}
                  >
                    <Button
                      onClick={() => void saveCatalog()}
                      disabled={catalogSaving || !catalogName.trim()}
                      className="flex items-center gap-2"
                      style={{ backgroundColor: BRAND_COLOR }}
                    >
                      {catalogSaving ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                          Salvando...
                        </>
                      ) : (
                        <>
                          <CheckIcon className="h-4 w-4" />
                          Criar
                        </>
                      )}
                    </Button>
                  </PermissionGuard>
                </div>
              </div>
            </div>
          )}
        </PermissionGuard>
      </div>
  )
}

export default MarketingParametrosPage
